import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  apiClient as defaultClient,
  type ApiClient,
  type ConnectedValidationSettingsView,
  type TestCredentialsView,
} from '../api/client';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { Spinner } from '../components/Spinner';
import { Button } from '../components/Button';
import { InlineAlert } from '../components/InlineAlert';
import { FormField } from '../components/FormField';
import { TextInput } from '../components/TextInput';
import { Checkbox } from '../components/Checkbox';
import { StatusBadge } from '../components/StatusBadge';
import { useOrg } from '../org/context';
import { useFocusMain } from '../a11y/useFocusMain';
import { toDisplayError, toFieldErrors } from '../api/errorMessages';
import { AuthedBrand } from './AuthedBrand';
import styles from './ProjectScreen.module.css';
import local from './ConnectedValidation.module.css';

type LoadState = 'loading' | 'error' | 'ready';
type Fields = Record<string, string | undefined>;

/** The write-only assurance copy (shared with the e2e spec via its testid). */
const ASSURANCE =
  "Credentials are encrypted at rest and never shown again. They're proven on your " +
  "first authorized run — there's no sign-in test here.";

const ACK_LABEL =
  'I confirm these are dedicated test-account credentials, not a real customer or staff login.';

/** Render the masked, no-overclaim status line for the live region. */
function describeStatus(view: TestCredentialsView): string {
  if (!view.configured) return 'No test credentials saved.';
  const when = view.updatedAt ? new Date(view.updatedAt).toLocaleDateString() : 'unknown date';
  const email = view.emailMasked ?? 'a saved test account';
  return `Saved — ${email} · updated ${when}. Password set.`;
}

export interface ConnectedValidationProps {
  client?: ApiClient;
}

/**
 * Connected Validation test-credentials setup (Story #430, slice 4). Org-admin
 * only. The email/password pair is WRITE-ONLY: submitted once over HTTPS and never
 * read back — the page only ever shows a masked summary. There is deliberately NO
 * "test connection" button (store-only): the pair is proven on the first authorized
 * run. A required dedicated-test-account acknowledgement must be checked before the
 * pair is stored. Replace re-opens the form; Remove clears the stored pair.
 */
export function ConnectedValidation({ client = defaultClient }: ConnectedValidationProps) {
  const { currentOrg } = useOrg();
  const { id: projectId } = useParams<{ id: string }>();
  const orgId = currentOrg?.id;
  const isAdmin = currentOrg?.role === 'org_admin';
  useFocusMain();

  const [phase, setPhase] = useState<LoadState>('loading');
  const [loadMessage, setLoadMessage] = useState('');
  const [credentials, setCredentials] = useState<TestCredentialsView>({
    configured: false,
    emailMasked: null,
    updatedAt: null,
  });
  const [settings, setSettings] = useState<ConnectedValidationSettingsView>({
    projectId: projectId ?? '',
    enabled: false,
    authorizedActions: [],
    updatedAt: null,
  });

  // The form is shown when nothing is saved yet, or when the admin chose Replace.
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [ack, setAck] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Fields>({});
  const [summary, setSummary] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const summaryRef = useRef<HTMLDivElement>(null);
  const successRef = useRef<HTMLDivElement>(null);
  const ackRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!orgId || !projectId || !isAdmin) return;
    setPhase('loading');
    try {
      const [creds, cvSettings] = await Promise.all([
        client.getTestCredentials(orgId, projectId),
        client.getConnectedValidationSettings(orgId, projectId),
      ]);
      setCredentials(creds);
      setSettings(cvSettings);
      setEditing(!creds.configured);
      setPhase('ready');
    } catch (err) {
      setLoadMessage(toDisplayError(err).message);
      setPhase('error');
    }
  }, [client, orgId, projectId, isAdmin]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (summary) summaryRef.current?.focus();
  }, [summary]);

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setAck(false);
    setFieldErrors({});
  };

  const validate = (): Fields => {
    const errs: Fields = {};
    if (!email.trim()) {
      errs.email = 'Enter the test-account email.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errs.email = 'Enter a valid email address.';
    }
    if (!password) errs.password = 'Enter the test-account password.';
    if (!ack) errs.ack = 'Please confirm these are dedicated test-account credentials.';
    return errs;
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy || !orgId || !projectId) return;
    setSuccess(null);

    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      setSummary('Please fix the errors below and try again.');
      // The ack is the safety gate — move focus to it when it is the blocker.
      if (errs.ack) requestAnimationFrame(() => ackRef.current?.focus());
      return;
    }
    setSummary(null);

    setBusy(true);
    try {
      const next = await client.setTestCredentials(orgId, projectId, {
        email: email.trim(),
        password,
      });
      setCredentials(next);
      resetForm(); // never retain the plaintext password (or email) after submit
      setEditing(false);
      setSuccess('Test credentials saved.');
      requestAnimationFrame(() => successRef.current?.focus());
    } catch (err) {
      const fields = toFieldErrors(err);
      if (Object.keys(fields).length > 0) {
        setFieldErrors(fields);
        setSummary('Please fix the errors below and try again.');
      } else {
        setSummary(toDisplayError(err).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const replace = () => {
    setSummary(null);
    setSuccess(null);
    resetForm();
    setEditing(true);
  };

  const remove = async () => {
    if (busy || !orgId || !projectId) return;
    setSummary(null);
    setSuccess(null);
    setBusy(true);
    try {
      await client.deleteTestCredentials(orgId, projectId);
      setCredentials({ configured: false, emailMasked: null, updatedAt: null });
      resetForm();
      setEditing(true);
      setSuccess('Test credentials removed.');
      requestAnimationFrame(() => successRef.current?.focus());
    } catch (err) {
      setSummary(toDisplayError(err).message);
    } finally {
      setBusy(false);
    }
  };

  // Tri-state readiness. A run needs BOTH an authorized boundary and saved
  // credentials; the held reason names the single most actionable next step.
  const runnable = settings.enabled && credentials.configured;
  const runnableStatus = runnable
    ? 'runnable'
    : !settings.enabled
      ? 'enable-boundary'
      : 'add-credentials';

  return (
    <AppShell brand={<AuthedBrand />}>
      <Card>
        <h1 className={styles.heading}>Connected validation credentials</h1>
        <p className={styles.subheading}>
          Store a dedicated test-account login so TestCraft can validate signed-in
          journeys on your storefront. We only ever sign in as a shopper — nothing in
          your store is changed.
        </p>

        {!isAdmin ? (
          <InlineAlert severity="info">
            Only an organization admin can configure test credentials.
          </InlineAlert>
        ) : (
          <>
            {phase === 'loading' ? (
              <div className={styles.loading}>
                <Spinner />
                <span>Loading test credentials…</span>
              </div>
            ) : null}

            {phase === 'error' ? (
              <div className={styles.loadError}>
                <InlineAlert severity="error">{loadMessage}</InlineAlert>
                <Link to={`/projects/${projectId ?? ''}/edit`} className={styles.backLink}>
                  Back to project
                </Link>
              </div>
            ) : null}

            {phase === 'ready' ? (
              <>
                <p className={styles.subheading} data-testid="connected-validation-assurance">
                  {ASSURANCE}
                </p>

                <div className={styles.summary}>
                  <InlineAlert ref={summaryRef} severity="error">
                    {summary}
                  </InlineAlert>
                </div>
                <InlineAlert ref={successRef} severity="success">
                  {success}
                </InlineAlert>

                <p aria-live="polite" data-testid="connected-validation-status">
                  {describeStatus(credentials)}
                </p>

                <dl className={local.tristate}>
                  <div className={local.tristateRow} data-testid="tristate-authorized">
                    <dt className={local.tristateLabel}>Boundary</dt>
                    <dd className={local.tristateValue}>
                      <StatusBadge
                        kind="connected-validation"
                        status={settings.enabled ? 'authorized' : 'not-authorized'}
                      />
                    </dd>
                  </div>
                  <div className={local.tristateRow} data-testid="tristate-credentials">
                    <dt className={local.tristateLabel}>Credentials</dt>
                    <dd className={local.tristateValue}>
                      <StatusBadge
                        kind="connected-validation"
                        status={credentials.configured ? 'credentials-saved' : 'not-saved'}
                      />
                    </dd>
                  </div>
                  <div className={local.tristateRow} data-testid="tristate-runnable">
                    <dt className={local.tristateLabel}>Readiness</dt>
                    <dd className={local.tristateValue}>
                      <StatusBadge kind="connected-validation" status={runnableStatus} />
                    </dd>
                  </div>
                </dl>

                {editing ? (
                  <form onSubmit={submit} noValidate>
                    <FormField
                      label="Test-account email"
                      id="cv-email"
                      helpText="The email of a dedicated test account — never a real customer or staff login."
                      {...(fieldErrors.email ? { error: fieldErrors.email } : {})}
                    >
                      {({ controlProps }) => (
                        <TextInput
                          {...controlProps}
                          type="email"
                          name="cv-email"
                          autoComplete="off"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      )}
                    </FormField>

                    <FormField
                      label="Test-account password"
                      id="cv-password"
                      helpText="Pasted once and stored encrypted. We never display it again."
                      {...(fieldErrors.password ? { error: fieldErrors.password } : {})}
                    >
                      {({ controlProps }) => (
                        <TextInput
                          {...controlProps}
                          type="password"
                          name="cv-password"
                          autoComplete="new-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                        />
                      )}
                    </FormField>

                    <InlineAlert severity="warning">
                      Use a dedicated test account. Never store a real customer or staff
                      login — TestCraft signs in with it on every authorized run.
                    </InlineAlert>

                    <Checkbox
                      ref={ackRef}
                      id="cv-ack"
                      data-testid="test-account-ack"
                      label={ACK_LABEL}
                      checked={ack}
                      onChange={(e) => {
                        setAck(e.target.checked);
                        if (e.target.checked) {
                          setFieldErrors((prev) => {
                            const rest = { ...prev };
                            delete rest.ack;
                            return rest;
                          });
                        }
                      }}
                      {...(fieldErrors.ack ? { error: fieldErrors.ack } : {})}
                    />

                    <div className={styles.actions}>
                      <Button type="submit" busy={busy}>
                        {busy ? 'Saving…' : 'Save credentials'}
                      </Button>
                      {credentials.configured ? (
                        <Button
                          type="button"
                          variant="ghost"
                          busy={busy}
                          onClick={() => {
                            resetForm();
                            setSummary(null);
                            setSuccess(null);
                            setEditing(false);
                          }}
                        >
                          Cancel
                        </Button>
                      ) : null}
                    </div>
                  </form>
                ) : (
                  <div className={styles.actions}>
                    <Button type="button" onClick={replace}>
                      Replace
                    </Button>
                    <Button type="button" variant="danger" busy={busy} onClick={remove}>
                      Remove
                    </Button>
                  </div>
                )}

                <Link to={`/projects/${projectId ?? ''}/edit`} className={styles.backLink}>
                  Back to project
                </Link>
              </>
            ) : null}
          </>
        )}
      </Card>
    </AppShell>
  );
}
